import React, { useEffect, useState, useCallback } from 'react'

// redux
import { useDispatch, useSelector } from 'react-redux'

import Button from '../../../../components/Button/Button'
import { formatNumber } from '../../../helpers/formatNumber'
import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine'
import ShimmerCircle from '../../../../components/shimmer-components/ShimmerCircle'
import MarketsListMobile from '../MarketsListMobile/MarketsListMobile'
import WenbitModal from '../../../../components/Modal/Modal'

// styles
import Styles from './marketInsight.module.css'

import { ReactComponent as UpArrowIcon } from '../../../../assets/icons/imported/up-arrow.svg'
import { resetLastTrade } from '../../redux/slices/tradesSlice';
import { useParams } from 'react-router-dom'

const convertCurrency = (toCurrency, fromCurrency, rate, amount) => {
  const formattedToCurrency = toCurrency?.toLowerCase()
  const formattedFromCurrency = fromCurrency?.toLowerCase()

  let convertedAmount
  if (formattedToCurrency === formattedFromCurrency) {
    convertedAmount = amount
  } else {
    convertedAmount = amount / rate
  }

  return convertedAmount
}

const convertCryptoCurrency = (toCurrency, fromCurrency, rate, amount) => {
  const formattedToCurrency = toCurrency?.toLowerCase()
  const formattedFromCurrency = fromCurrency?.toLowerCase()

  let convertedAmount
  if (formattedToCurrency === formattedFromCurrency) {
    convertedAmount = amount
  } else {
    convertedAmount = amount * rate
  }

  return convertedAmount
}

function MarketInsight() {
  // ** Hooks
  const dispatch = useDispatch()
  const { marketId } = useParams()
  // ** States
  const selectedMarket = useSelector(state => state.selectedMarket.value)
  const marketTickers = useSelector(state => state.marketTickers.value)
  // const tickers = marketTickers[selectedMarket.id]
  const marketsList = useSelector(state => state.marketList.value)
  const lastTrade = useSelector(state => state.trades.last)
  // console.log(useSelector((state) => state.selectedMarket.value))
  const selectedMarketDetails = selectedMarket ? marketTickers[selectedMarket.id] : marketTickers[marketId]
  const currencies = useSelector(state => state.currencies.value)
  const globalConversions = useSelector(state => state.globalConversions.value)
  const preferredCurrency = useSelector(state => state.preferredCurrency.value)
  const theme = useSelector(state => state.theme.value)
  const [selectPairModalOpen, setSelectPairModalOpen] = useState(false)
  const [hideMarketsList, setHideMarketsList] = useState(window.innerWidth > 990)
  const [convertedPrice, setConvertedPrice] = useState(null)
  const [targetedMarket, setTargetedMarket] = useState(null)
  const [cryptoCurrencies, setCryptoCurrencies] = useState([])
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const handleResize = useCallback(() => {
    const width = window.innerWidth
    setHideMarketsList(width > 990)
    setWindowWidth(width)
  }, [])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  useEffect(() => {
    setTargetedMarket(marketsList?.find(el => el.id === selectedMarket.id))
    // dispatch(resetLastTrade())
  }, [selectedMarket, marketsList, dispatch])

  useEffect(() => {
    dispatch(resetLastTrade())
  }, [selectedMarket])

  useEffect(() => {
    if (currencies.length > 0) {
      const crypto = currencies.filter(currency => currency.type === 'coin').map(currency => currency.id)
      setCryptoCurrencies(crypto)
    }
    // console.log(selectedMarketDetails)
  }, [currencies])

  useEffect(() => {
    if (lastTrade?.price && selectedMarket && cryptoCurrencies.length && globalConversions?.length) {
      const quoteCode = selectedMarket?.name?.split('/')[1]
      const isCryptoCurrency = cryptoCurrencies.includes(quoteCode?.toLowerCase())
      let priceInPreferredCurrency

      if (isCryptoCurrency && quoteCode !== 'USD') {
        const cryptoToUsdRate = globalConversions?.find(
          rate => rate.currency_from === quoteCode && rate.currency_to === 'USD'
        )?.rate

        if (cryptoToUsdRate) {
          const priceInUsd = convertCryptoCurrency(preferredCurrency.id, 'USD', cryptoToUsdRate, lastTrade.price)
          const usdToPreferredRate = globalConversions?.find(
            rate => rate.currency_from === 'USD' && rate.currency_to === preferredCurrency.id
          )?.rate

          if (usdToPreferredRate) {
            priceInPreferredCurrency = convertCurrency(preferredCurrency.id, 'USD', usdToPreferredRate, priceInUsd)
          } else {
            const preferredRateToUsd = globalConversions?.find(
              rate => rate.currency_to === 'USD' && rate.currency_from === preferredCurrency.id
            )?.rate
            priceInPreferredCurrency = convertCurrency('USD', preferredCurrency.id, preferredRateToUsd, priceInUsd)
          }
        }
        // } else if (quoteCode === "USDT" && preferredCurrency.id === "USD") {
        //   const rate = globalConversions?.find(
        //     (rate) => rate.currency_from === "USDT" && rate.currency_to === preferredCurrency.id
        //   )?.rate
        //   priceInPreferredCurrency = (lastTrade.price * rate)
      } else {
        const usdToPreferredRate = globalConversions?.find(
          rate => rate.currency_from === 'USD' && rate.currency_to === preferredCurrency.id
        )?.rate

        if (usdToPreferredRate) {
          priceInPreferredCurrency = convertCurrency(preferredCurrency.id, 'USD', usdToPreferredRate, lastTrade.price)
        } else {
          const preferredRateToUsd = globalConversions?.find(
            rate => rate.currency_to === 'USD' && rate.currency_from === preferredCurrency.id
          )?.rate
          priceInPreferredCurrency = convertCurrency('USD', preferredCurrency.id, preferredRateToUsd, lastTrade.price)
        }
      }
      setConvertedPrice(priceInPreferredCurrency)
    }
  }, [lastTrade, selectedMarket, preferredCurrency.id, globalConversions, cryptoCurrencies])

  const formattedPrice = price => {
    return `${preferredCurrency.symbol}${formatNumber(price)}`
  }

  const calculateRangePosition = () => {
    if (!selectedMarketDetails?.high || !selectedMarketDetails?.low || !lastTrade?.price) return 0
    const range = selectedMarketDetails.high - selectedMarketDetails.low
    return ((lastTrade.price - selectedMarketDetails.low) / range) * 100
  }

  const handleStatCardHover = e => {
    e.currentTarget.style.background = theme === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'
    e.currentTarget.style.transform = 'translateY(-2px)'
  }

  const handleStatCardLeave = e => {
    e.currentTarget.style.background = theme === 'light' ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.03)'
    e.currentTarget.style.transform = 'translateY(0)'
  }

  // Determine which sections to show based on window width
  const showStatsSection = windowWidth > 1200
  const showRangeSection = windowWidth > 1500

  return (
    <div className={`${Styles.marketWidget} ${theme === 'light' ? Styles.light : ''}`}>
      {/* Coin Info Section */}
      <div className={Styles.coinSection}>
        {targetedMarket ? (
          <div className={Styles.coinIcon}>
            <img src={targetedMarket?.icon_url} alt={targetedMarket?.coin_name} />
          </div>
        ) : (
          <ShimmerCircle size='45px' />
        )}
        <div>
          {targetedMarket ? (
            <div className={Styles.coinName}>{targetedMarket?.coin_name.toUpperCase()}</div>
          ) : (
            <ShimmerLine height='18px' width='80px' />
          )}
          {targetedMarket && (
            <div className={Styles.coinPair}>
              {targetedMarket?.name?.split('/')[0]}/{targetedMarket?.name?.split('/')[1]}
            </div>
          )}
        </div>
      </div>

      {/* Price Section */}
      <div className={Styles.priceSectionContainer}>
        <div className={Styles.priceSection}>
          <div className={Styles.statLabel}>
            <span>
              {targetedMarket?.coin_name?.charAt(0).toUpperCase() + targetedMarket?.coin_name?.slice(1)} Price (USD)
            </span>
            {/* <span className={Styles.infoIcon}>i</span> */}
          </div>
          {lastTrade?.price ? (
            <div
              className={Styles.statValue}
              style={{
                color: lastTrade.taker_type === 'buy' ? 'var(--negative-color)' : 'var(--positive-color)'
              }}
            >
              {formatNumber(lastTrade?.price)}
            </div>
          ) : (
            <ShimmerLine height='24px' width='120px' />
          )}
        </div>
        <div className={Styles.verticalDivider}></div>
      </div>

      {/* 24h Change Section */}
      <div className={Styles.changeSection}>
        <div className={Styles.statLabel}>
          <span>24h Change</span>
        </div>
        {selectedMarketDetails?.price_change_percent && (
          <div
            className={Styles.statValue}
            style={{
              color:
                selectedMarketDetails?.price_change_percent[0] === '+'
                  ? 'var(--positive-color)'
                  : 'var(--negative-color)'
            }}
          >
            <span>{selectedMarketDetails?.price_change_percent[0] === '+' ? '↑' : '↓'}</span>
            <span>{Math.abs(parseFloat(selectedMarketDetails?.price_change_percent))}%</span>
          </div>
        )}
      </div>

      {/* Stats Section - Hidden when width <= 1500px */}
      {showStatsSection && (
        <div className={Styles.statsSection}>
          <div className={Styles.statItem}>
            <div className={Styles.statLabel}>
              {selectedMarket?.name} Amount
              {/* <span className={Styles.infoIcon}>i</span> */}
            </div>
            <div className={Styles.statValue}>
              {selectedMarketDetails?.amount ? parseFloat(selectedMarketDetails?.amount).toFixed(4) : 'Loading...'}
            </div>
          </div>

          <div className={Styles.statItem}>
            <div className={Styles.statLabel}>
              {selectedMarket?.name} Volume
              {/* <span className={Styles.infoIcon}>i</span> */}
            </div>
            <div className={Styles.statValue}>
              {selectedMarketDetails?.volume ? parseFloat(selectedMarketDetails?.volume).toFixed(4) : 'Loading...'}
            </div>
          </div>
        </div>
      )}

      {/* Price Range Section - Hidden when width <= 1400px */}
      {showRangeSection && (
        <div className={Styles.rangeSection}>
          <div className={Styles.rangeHeader}>
            <span>��</span>
            <span>High/Low Price</span>
            {/* <span className={Styles.infoIcon}>i</span> */}
          </div>
          <div className={Styles.rangeBarContainer}>
            <div className={Styles.rangeBar}>
              <div className={Styles.rangeFill} style={{ width: '100%' }}></div>
              <div className={Styles.rangeIndicator} style={{ left: `${calculateRangePosition()}%` }}></div>
            </div>
          </div>
          <div className={Styles.rangeValues}>
            <span className={Styles.rangeLow}>
              Low: {selectedMarketDetails?.low ? formatNumber(selectedMarketDetails?.low) : 'Loading...'}
            </span>
            <span className={Styles.rangeHigh}>
              High: {selectedMarketDetails?.high ? formatNumber(selectedMarketDetails?.high) : 'Loading...'}
            </span>
          </div>
        </div>
      )}

      {/* Select Pair Button for Mobile */}
      {!hideMarketsList && (
        <div className={Styles.selectPairContainer}>
          <Button
            className={Styles.selectPairButton}
            text='Select Pair'
            icon={<UpArrowIcon style={{ transform: 'rotateX(180deg)' }} />}
            iconPosition='right'
            variant='link'
            onClick={() => setSelectPairModalOpen(true)}
          />
        </div>
      )}

      {/* MODALS */}
      <WenbitModal
        open={selectPairModalOpen}
        title='PAIRS'
        render={() => <MarketsListMobile setCloseModal={setSelectPairModalOpen} />}
        onClose={() => setSelectPairModalOpen(false)}
      />
    </div>
  )
}

export default MarketInsight
